// Offline Tracking Control — an unauthenticated, no-server-call screen that
// always opens, even when:
//   - The bundled shell is loaded (no remote available)
//   - The user is offline (Airplane Mode, ger camp, captive portal)
//   - Supabase auth hasn't resolved yet
//
// All navigation here is router/client-state only. We deliberately do NOT use
// window.location.href / .replace to bounce to this route — that triggers a
// WKWebView load against the remote origin and fails with NSURLErrorDomain
// -1009 while offline.

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { pageMeta } from "@/lib/seo";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  MapPin,
  Play,
  Pause,
  Square,
  RefreshCw,
  Wifi,
  WifiOff,
  CloudOff,
  CloudUpload,
  BatteryCharging,
  BatteryLow,
  Battery,
  Zap,
  Check,
  Loader2,
  Activity,
  Crosshair,
  Settings as SettingsIcon,
  Gauge,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  setTrackingState,
  useTrackingState,
  useTrackingStartedAt,
  useTrackingPhase,
} from "@/lib/native/tracking-state";
import { startActiveWatch, stopActiveWatch, reapplyActiveWatchAdaptive, pauseActiveWatch, resumeActiveWatch } from "@/lib/native/active-watch";
import { getShellMode, onShellModeChange, reloadShellFromRemote, type ShellMode, type ShellError } from "@/lib/native/shell-mode";
import { humanizeShellError } from "@/lib/native/shell-error-copy";
import {
  getGpsPreset,
  setGpsPreset,
  GPS_PRESETS,
  getGpsAdaptive,
  getBatteryGuardMode,
  getBatteryGuardThresholdPct,
  getTrackingAllowWhenCharging,
  type GpsPreset,
  type GpsAdaptive,
} from "@/lib/prefs";

import { useCachedTrip, hydrateCachedTrip } from "@/lib/offline/trip-cache";
import {
  useLocalUploadRules,
  setLocalUploadRules,
  hydrateLocalUploadRules,
} from "@/lib/offline/upload-rules-local";
import { reconcileSettingsNow } from "@/lib/offline/settings-sync";
import { supabase } from "@/integrations/supabase/client";
import { OfflineTrackingStatus } from "@/components/OfflineTrackingStatus";
import { PingReconciliationCard } from "@/components/PingReconciliationCard";
import { OfflineMapsCatalog } from "@/components/OfflineMapsCatalog";
import { getBatteryInfo, type BatteryInfo } from "@/lib/native/battery";
import { useOutboxStats } from "@/lib/native/use-outbox-stats";
import { drainPingOutbox } from "@/lib/native/ping-outbox";
import { runSyncNow } from "@/lib/native/run-sync";
import { resumeSyncFlow } from "@/lib/auth/resume-sync";
import { drainNativeLocationQueue } from "@/lib/native/location-bg";
import { useNativeQueueStats } from "@/lib/native/tracking-reliability";
import { useGpsStatus } from "@/lib/native/gps-status";
import {
  addConnectionListener,
  getConnectionType,
  type ConnectionType,
} from "@/lib/native/network";
import { useLocationPermission } from "@/lib/native/location-permission";
import { BatteryGuardCard } from "@/components/permissions/BatteryGuardCard";
import { useBatteryGuard } from "@/lib/native/use-battery-guard";
import { ManualGpsButton } from "@/components/ManualGpsButton";
import {
  RoavaPage,
  RoavaCard,
  StatusChip,
  type StatusChipVariant,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  
} from "@/components/roava";
import { cn } from "@/lib/utils";
import { ShellDiagnosticStamp } from "@/components/ShellDiagnosticStamp";

export const Route = createFileRoute("/offline-control")({
  head: () =>
    pageMeta({
      title: "Offline settings — Roava",
      description: "Control tracking and upload settings even when offline.",
      path: "/offline-control",
    }),
  component: OfflineControl,
});

// Hiking-focused presets shown on this screen. "Plane" is intentionally
// omitted here — it lives on the regular tracking settings page.
const HIKING_PRESETS: Array<{
  id: Extract<GpsPreset, "battery_saver" | "balanced" | "high_detail" | "expedition">;
  label: string;
  description: string;
}> = [
  { id: "battery_saver", label: "Battery saver", description: "Longer battery life with fewer saved points." },
  { id: "balanced",      label: "Balanced",      description: "Good default for most travel days." },
  { id: "high_detail",   label: "High accuracy", description: "More detail, higher battery use." },
  { id: "expedition",    label: "Expedition",    description: "Best for multi-day hikes. About one fix every 30 minutes while stopped." },
];

function presetLabel(p: GpsPreset): string {
  const hit = HIKING_PRESETS.find((x) => x.id === p);
  if (hit) return hit.label;
  if (p === "plane") return "Plane";
  return "Custom";
}

function formatDistance(m: number): string {
  if (m >= 1000) {
    const km = m / 1000;
    return km >= 10 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
  }
  return `${Math.round(m)} m`;
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `~${min} min`;
  return `~${Math.round(min / 60)} h`;
}

function nextPingPrimary(preset: GpsPreset, a: GpsAdaptive): string {
  if (preset === "plane") return "when movement resumes";
  if (preset === "custom") return "based on current preset";
  return `in ${formatDistance(a.movingDistanceM)} of movement`;
}

function nextPingSecondary(preset: GpsPreset, a: GpsAdaptive): string | null {
  if (preset === "plane" || preset === "custom") return null;
  return `or ${formatDuration(a.stoppedIntervalMs)} while stopped`;
}

function fmtAgoMs(ms: number | null, nowMs: number): string {
  if (ms == null) return "—";
  const s = Math.max(0, Math.floor((nowMs - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtAgoIso(iso: string | null, nowMs: number): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const s = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function useBatteryInfo(): BatteryInfo {
  const [info, setInfo] = useState<BatteryInfo>({ level: null, isCharging: null });
  useEffect(() => {
    let alive = true;
    const read = () => { void getBatteryInfo().then((v) => { if (alive) setInfo(v); }); };
    read();
    const id = window.setInterval(read, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") read(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return info;
}

function useConnectionType(): ConnectionType {
  const [t, setT] = useState<ConnectionType>("unknown");
  useEffect(() => {
    let alive = true;
    void getConnectionType().then((v) => { if (alive) setT(v); });
    const sub = addConnectionListener(setT);
    const refresh = () => void getConnectionType().then((v) => { if (alive) setT(v); });
    if (typeof window !== "undefined") {
      window.addEventListener("online", refresh);
      window.addEventListener("offline", refresh);
    }
    return () => {
      alive = false;
      sub.remove();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", refresh);
        window.removeEventListener("offline", refresh);
      }
    };
  }, []);
  return t;
}

export function OfflineControl() {
  const trip = useCachedTrip();
  const tracking = useTrackingState();
  const startedAt = useTrackingStartedAt();
  const trackingPhase = useTrackingPhase();
  const nav = useNavigate();
  const [mode, setMode] = useState<ShellMode>("remote");
  const [shellError, setShellError] = useState<ShellError | null>(null);
  const [preset, setPresetState] = useState<GpsPreset>(getGpsPreset());
  const [gpsPresetOpen, setGpsPresetOpen] = useState(false);
  const [uploadRulesOpen, setUploadRulesOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [lastSyncAttempt, setLastSyncAttempt] = useState<{ at: string; ok: boolean; reason?: string } | null>(null);

  async function handleResumeSync() {
    if (resuming) return;
    setResuming(true);
    try {
      await resumeSyncFlow((path) => nav({ to: path as "/login" }));
    } finally {
      setResuming(false);
    }
  }



  const rules = useLocalUploadRules(trip?.id ?? null);
  const outbox = useOutboxStats();
  const nativeQueueStats = useNativeQueueStats();
  const gps = useGpsStatus();
  const battery = useBatteryInfo();
  const conn = useConnectionType();
  const perm = useLocationPermission();
  const linkUp = conn !== "none" && (typeof navigator === "undefined" || navigator.onLine !== false);
  const online = linkUp;

  // 1s tick so relative times update while mounted.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Authoritative server-side ping count for the active trip — lets users
  // reconcile on-device queue counts (which include retries) against what
  // actually landed in the cloud after server-side dedup.
  const [cloudPings, setCloudPings] = useState<{ count: number; latestAt: string | null } | null>(null);
  useEffect(() => {
    const tripId = trip?.id;
    if (!tripId || !online) return;
    let alive = true;
    const load = () => {
      void import("@/lib/sync/trip-cloud-count").then(({ fetchTripCloudPingCount }) =>
        fetchTripCloudPingCount(tripId).then((s) => {
          if (alive) setCloudPings(s);
        }).catch(() => {})
      );
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [trip?.id, online, outbox.lastSyncAt]);

  useEffect(() => {
    // v4.9 — also drain native LocationBG queue on mount.
    void hydrateCachedTrip().then((t) => {
      void drainNativeLocationQueue(t?.id ?? null);
    });
    void hydrateLocalUploadRules();
    void getShellMode().then((s) => { setMode(s.mode); setShellError(s.lastError); });
    const off = onShellModeChange((s) => { setMode(s.mode); setShellError(s.lastError); });
    return () => { off(); };
  }, []);

  async function start() {
    if (!trip?.id) {
      toast.error("No cached trip — connect once first.");
      return;
    }
    try {
      await setTrackingState(true);
      await startActiveWatch(trip.id);
      toast.success("Tracking started.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start tracking");
    }
  }

  async function stop() {
    try {
      // Intentional user-driven stop-tracking action from Offline Controls.
      // Ends the Live Activity by design — source-tagged so the log makes
      // the reason obvious.
      await setTrackingState(false);
      await stopActiveWatch({ source: "offline-control-stop" });
      toast.success("Tracking stopped.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not stop tracking");
    }
  }

  async function pause() {
    try {
      await pauseActiveWatch({ source: "offline-control-pause" });
      toast.success("Tracking paused.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not pause tracking");
    }
  }

  async function resume() {
    try {
      await resumeActiveWatch({ source: "offline-control-resume" });
      toast.success("Tracking resumed.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not resume tracking");
    }
  }



  // Tracking Battery Guard: same warn/pause behavior as the Tracking screen.
  // Mounting this here means the guard polls and runs an immediate check
  // whenever the user opens Offline Controls.
  useBatteryGuard({ tracking, startedAt, stop });


  async function tryReconnect() {
    setRetrying(true);
    try {
      const [shell] = await Promise.all([
        reloadShellFromRemote(),
        supabase.auth.refreshSession().catch(() => null),
      ]);
      void reconcileSettingsNow();
      if (shell.mode === "remote") toast.success("Reconnected.");
      else toast("Still offline — try again in a moment.");
    } finally {
      setRetrying(false);
    }
  }

  async function syncNow() {
    if (syncing) return;
    setSyncing(true);
    try {
      await runSyncNow({ navigate: (p: string) => nav({ to: p as "/login" }) });
      setLastSyncAttempt({ at: new Date().toISOString(), ok: true });
    } finally {
      setSyncing(false);
    }
  }


  function pickPreset(p: GpsPreset) {
    setGpsPreset(p);
    setPresetState(p);
    void (async () => {
      try {
        const wasRunning = await reapplyActiveWatchAdaptive();
        if (wasRunning) toast.success("GPS rate updated");
      } catch (e) {
        console.warn("[offline-control] reapply failed", e);
      }
    })();
  }

  async function patchRules(patch: Partial<typeof rules>, opts?: { silent?: boolean }) {
    if (!trip?.id) {
      toast.error("Settings need a cached trip first.");
      return;
    }
    await setLocalUploadRules(patch, trip.id);
    if (!opts?.silent) {
      const online = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
      toast.success(online ? "Saved on this device" : "Saved on this device · syncs when online");
    }
    if (typeof navigator !== "undefined" && navigator.onLine !== false) {
      void reconcileSettingsNow();
    }
  }

  function onWifiOnlyChange(v: boolean) {
    void patchRules({ wifiOnly: v }, { silent: true });
    if (v && conn !== "wifi") {
      toast.warning("Wi-Fi only is on — uploads paused until Wi-Fi returns.");
    } else {
      toast.success("Saved on this device");
    }
  }

  function onUploadModeChange(m: "auto" | "manual") {
    if (rules.uploadMode === m) return;
    void patchRules({ uploadMode: m }, { silent: true });
    if (m === "manual") {
      toast.warning("Manual mode — automatic background uploads are off.");
    } else {
      toast.success("Automatic uploads on.");
    }
  }

  function onMinBatteryCommit(v: number) {
    void patchRules({ minBatteryPct: v }, { silent: true });
    const cur = battery.level == null ? null : Math.round(battery.level * 100);
    const charging = battery.isCharging === true;
    if (
      cur != null &&
      v > cur &&
      !(charging && rules.requireChargingUnderMin)
    ) {
      toast.warning(`Uploads paused — battery ${cur}% below ${v}%.`);
    } else {
      const online = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
      toast.success(online ? "Saved on this device" : "Saved on this device · syncs when online");
    }
  }


  // Battery view-model.
  const hasBattery = battery.level != null;
  const batteryPct = hasBattery ? Math.round((battery.level as number) * 100) : null;
  const lowBattery = hasBattery && (batteryPct as number) < 15 && battery.isCharging !== true;

  const adaptive = getGpsAdaptive();
  const nextPrimary = nextPingPrimary(preset, adaptive);
  const nextSecondary = nextPingSecondary(preset, adaptive);

  // Last ping proof-of-life: prefer the live GPS watcher's last fix over the
  // outbox (which only updates on FAILED sends). The watcher updates on every
  // successful position, so this is the honest heartbeat signal.
  const hasFix = gps.lastFixAt != null;
  const lastPingText = hasFix
    ? `${fmtAgoMs(gps.lastFixAt, now)}${gps.lastAccuracy != null ? ` · ±${Math.round(gps.lastAccuracy)} m` : ""} · GPS`
    : tracking ? "Waiting for fix…" : "—";

  // Single network chip — exactly one of Online / Offline / Captive portal /
  // Waiting for internet. We don't have a separate reachability signal on
  // this screen (OfflineTrackingStatus owns that), so chip uses link state.
  const netChip: { label: string; variant: StatusChipVariant; Icon: React.ComponentType<{ className?: string }> } =
    !linkUp
      ? { label: "Offline", variant: "warning", Icon: WifiOff }
      : { label: "Online", variant: "success", Icon: Wifi };

  return (
    <div data-roava-route="offline-control">
      <RoavaPage
        title="Offline settings"
        subtitle="Sync now, Wi-Fi-only uploads, and on-device queue controls. Use this when Roava can't reach the internet — tracking continues locally and syncs when you're back online."
        showLogo={false}
        back={{ to: "/my-uploads", label: "Back to uploads" }}
      >
        {/* Single network status chip */}
        <div className="flex flex-wrap gap-2 mb-6">
          <StatusChip variant={netChip.variant} icon={<netChip.Icon className="size-3.5" />}>
            {netChip.label}
          </StatusChip>
          {rules.dirty && !linkUp && (
            <StatusChip variant="info" icon={<CloudOff className="size-3.5" />}>
              Saved on device · syncs when online
            </StatusChip>
          )}
          {rules.dirty && linkUp && (
            <StatusChip variant="info" icon={<Loader2 className="size-3.5 animate-spin" />}>
              Syncing settings…
            </StatusChip>
          )}
        </div>

        <div className="space-y-4">
          {/* 0a. Shell diagnostic stamp — proves hosted vs bundled */}
          <ShellDiagnosticStamp />

          {/* 0. Offline maps catalog — search, download, delete */}
          <OfflineMapsCatalog />

          {/* 1. Tracking status panel (includes captive-portal banner) */}
          <OfflineTrackingStatus />

          {/* 1b. Ping reconciliation — explains where any missing pings live. */}
          <PingReconciliationCard tripId={trip?.id ?? null} />


          {/* 2. Tracking action card */}
          <RoavaCard>
            <div
              className={cn(
                "mx-auto mb-3 size-16 rounded-full grid place-items-center",
                tracking
                  ? "bg-[var(--roava-green)]/15 text-[var(--roava-green)] animate-pulse"
                  : "bg-[var(--roava-gray)] text-[var(--roava-text-muted)]",
              )}
            >
              <MapPin className="size-8" />
            </div>
            <p className="text-center text-lg font-semibold text-[var(--roava-text)]">
              {trackingPhase === "active" ? "Route tracking active" : trackingPhase === "paused" ? "Tracking paused" : "Not tracking"}
            </p>
            <p className="text-center text-xs text-[var(--roava-text-muted)] mt-1">
              {trip?.name ? `Trip: ${trip.name}` : "No cached trip yet"}
              {tracking && startedAt && ` · since ${new Date(startedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`}
            </p>

            {trip?.id ? (
              <div className="mt-5 grid gap-3">
                {trackingPhase === "off" && (
                  <PrimaryButton
                    onClick={start}
                    className="h-14 w-full rounded-2xl text-base font-semibold"
                  >
                    <Play className="size-5 mr-2" />Start tracking
                  </PrimaryButton>
                )}
                {trackingPhase === "active" && (
                  <SecondaryButton
                    onClick={pause}
                    className="h-14 w-full rounded-2xl text-base font-semibold"
                  >
                    <Pause className="size-5 mr-2" />Pause tracking
                  </SecondaryButton>
                )}
                {trackingPhase === "paused" && (
                  <PrimaryButton
                    onClick={resume}
                    className="h-14 w-full rounded-2xl text-base font-semibold"
                  >
                    <Play className="size-5 mr-2" />Resume tracking
                  </PrimaryButton>
                )}
                {tracking && <ManualGpsButton tripId={trip.id} />}
                {trackingPhase !== "off" && (
                  <div className="mt-2 pt-3 border-t border-[var(--roava-border)] grid gap-1.5">
                    <DangerButton
                      onClick={stop}
                      className="h-12 w-full rounded-2xl text-sm font-semibold"
                    >
                      <Square className="size-4 mr-2" />Stop tracking
                    </DangerButton>
                    <p className="text-[11px] leading-snug text-[var(--roava-text-muted)] text-center px-2">
                      Ends this tracking session. Pause keeps the session alive — Stop closes it.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 rounded-2xl bg-[var(--roava-gray)]/60 p-4 text-sm text-center">
                <p className="font-medium mb-1 text-[var(--roava-text)]">Connect to set up your first trip</p>
                <p className="text-xs text-[var(--roava-text-muted)]">
                  Roava needs one online launch to learn which trip you're on. After that, tracking works offline.
                </p>
              </div>
            )}
          </RoavaCard>

          {/* 3. Tracker activity */}
          <RoavaCard>
            <div className="flex items-center gap-2 mb-3">
              <Activity className={cn("size-4", tracking ? "text-[var(--roava-green)]" : "text-[var(--roava-text-muted)]")} />
              <h2 className="text-sm font-semibold flex-1 text-[var(--roava-text)]">
                {tracking ? "Tracker activity" : "Tracker"}
              </h2>
              {tracking && (
                <StatusChip
                  variant="success"
                  icon={<span className="size-1.5 rounded-full bg-[var(--roava-green)] animate-pulse" />}
                >
                  Live
                </StatusChip>
              )}
            </div>
            <div className="space-y-1.5 text-xs">
              <KV label="Last GPS fix" value={lastPingText} />
              <div className="flex items-start justify-between gap-3">
                <span className="text-[var(--roava-text-muted)]">Next automatic route point</span>
                <div className="text-right">
                  <div className="font-medium text-[var(--roava-text)]">{nextPrimary}</div>
                  {nextSecondary && (
                    <div className="text-[11px] text-[var(--roava-text-muted)]/80">{nextSecondary}</div>
                  )}
                </div>
              </div>
              <KV label="GPS preset" value={presetLabel(preset)} />
              <KV label="Saved on this device" value="Yes" />
            </div>
            <p className="text-[11px] text-[var(--roava-text-muted)] mt-3 leading-snug">
              iOS controls when background location updates arrive. Roava saves eligible route points locally and syncs them when internet returns.
            </p>
          </RoavaCard>

          {/* 4. Tracking settings — visually distinct group. */}
          <section className="space-y-3">
            <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--roava-text-muted)] flex items-center gap-2">
              <SettingsIcon className="size-3.5" />
              Tracking settings
            </h2>

            {/* GPS preset — kept as button grid in Phase 2; Phase 4 will migrate. */}
            <RoavaCard>
              <button
                type="button"
                onClick={() => setGpsPresetOpen((v) => !v)}
                aria-expanded={gpsPresetOpen}
                className="w-full flex items-center gap-2 text-left"
              >
                <Activity className="size-4 text-[var(--roava-text-muted)]" />
                <h3 className="text-sm font-semibold text-[var(--roava-text)]">GPS preset</h3>
                <span className="ml-auto text-xs text-[var(--roava-text-muted)]">
                  {presetLabel(preset)}
                  {GPS_PRESETS[preset as keyof typeof GPS_PRESETS]
                    ? ` · ${formatDistance(GPS_PRESETS[preset as keyof typeof GPS_PRESETS].movingDistanceM)} moving`
                    : ""}
                </span>
                {gpsPresetOpen
                  ? <ChevronDown className="size-4 text-[var(--roava-text-muted)]" />
                  : <ChevronRight className="size-4 text-[var(--roava-text-muted)]" />}
              </button>

              {gpsPresetOpen && (
                <div className="mt-3">
                  <div className="grid gap-2">
                    {HIKING_PRESETS.map((p) => {
                      const active = preset === p.id;
                      const a = GPS_PRESETS[p.id];
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => pickPreset(p.id)}
                          className={cn(
                            "w-full text-left rounded-2xl border p-3 transition",
                            active
                              ? "border-[var(--roava-blue)] bg-[var(--roava-blue)]/5"
                              : "border-[var(--roava-border)] bg-[var(--roava-bg)] hover:bg-[var(--roava-gray)]/40",
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-[var(--roava-text)]">{p.label}</p>
                                {active && <Check className="size-4 text-[var(--roava-blue)]" />}
                              </div>
                              <p className="text-xs text-[var(--roava-text-muted)] mt-0.5">{p.description}</p>
                              <p className="text-[11px] text-[var(--roava-text-muted)]/80 mt-1 font-mono">
                                {formatDistance(a.movingDistanceM)} moving · {formatDuration(a.stoppedIntervalMs)} stopped
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[var(--roava-text-muted)] mt-3">
                    Expedition is recommended for multi-day hiking with limited charging.
                  </p>
                  <p className="text-[11px] text-[var(--roava-text-muted)] mt-1">
                    Changes apply right away — tracking re-arms in the background if it's running.
                  </p>
                </div>
              )}
            </RoavaCard>

            {/* Tracking Battery Guard — mounted unchanged. Protects ROUTE
                TRACKING, not uploads. */}
            <RoavaCard>
              <h3 className="text-sm font-semibold text-[var(--roava-text)]">Tracking battery guard</h3>
              <p className="text-[11px] text-[var(--roava-text-muted)] mt-1 mb-2">
                Controls whether GPS route tracking pauses when battery is low. Separate from upload rules.
              </p>
              <BatteryGuardCard />
              <GuardStateNote tracking={tracking} batteryPct={batteryPct} charging={battery.isCharging === true} />
            </RoavaCard>

          </section>

          {/* 5. Upload & sync settings — visually distinct group. */}
          <section className="space-y-3">
            <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--roava-text-muted)] flex items-center gap-2">
              <CloudUpload className="size-3.5" />
              Upload & sync settings
            </h2>

            <RoavaCard>
              <button
                type="button"
                onClick={() => setUploadRulesOpen((v) => !v)}
                aria-expanded={uploadRulesOpen}
                className="w-full flex items-center gap-2 text-left"
              >
                <CloudUpload className="size-4 text-[var(--roava-text-muted)]" />
                <h3 className="text-sm font-semibold text-[var(--roava-text)]">Upload rules</h3>
                <span className="ml-auto text-xs text-[var(--roava-text-muted)] truncate max-w-[55%]">
                  {rules.uploadMode === "auto" ? "Automatic" : "Manual"}
                  {" · "}{rules.wifiOnly ? "Wi-Fi only" : "Any network"}
                  {" · "}{rules.minBatteryPct === 0 ? "no min" : `${rules.minBatteryPct}%`}
                </span>
                {uploadRulesOpen
                  ? <ChevronDown className="size-4 text-[var(--roava-text-muted)]" />
                  : <ChevronRight className="size-4 text-[var(--roava-text-muted)]" />}
              </button>

              {uploadRulesOpen && (
                <div className="mt-3">
                  <p className="text-[11px] text-[var(--roava-text-muted)] mb-2">
                    These settings only affect uploading photos, videos, and queued data. They do not control route tracking.
                  </p>

                  <Row>
                    <div className="flex items-center gap-2">
                      <Wifi className="size-4 text-[var(--roava-text-muted)]" />
                      <span className="text-sm font-medium text-[var(--roava-text)]">Wi-Fi only</span>
                    </div>
                    <Switch
                      checked={rules.wifiOnly}
                      onCheckedChange={onWifiOnlyChange}
                    />
                  </Row>
                  {rules.wifiOnly && conn !== "wifi" && (
                    <p className="-mt-1 pb-1 text-[11px] text-[var(--roava-orange)]">
                      Not on Wi-Fi — uploads are paused until Wi-Fi returns.
                    </p>
                  )}

                  <Row>
                    <div className="flex items-center gap-2">
                      <Zap className="size-4 text-[var(--roava-text-muted)]" />
                      <span className="text-sm font-medium text-[var(--roava-text)]">Upload mode</span>
                    </div>
                    <div className="flex gap-1">
                      <ModeBtn active={rules.uploadMode === "auto"} onClick={() => onUploadModeChange("auto")}>Automatic</ModeBtn>
                      <ModeBtn active={rules.uploadMode === "manual"} onClick={() => onUploadModeChange("manual")}>Manual</ModeBtn>
                    </div>
                  </Row>

                  <div className="py-3 border-t border-[var(--roava-border)]">
                    <div className="flex items-center gap-2 mb-2">
                      <BatteryCharging className="size-4 text-[var(--roava-text-muted)]" />
                      <label className="text-sm font-medium flex-1 text-[var(--roava-text)]">Min battery</label>
                      <span className="text-sm font-mono tabular-nums text-[var(--roava-text)]">
                        {rules.minBatteryPct === 0 ? "off" : `${rules.minBatteryPct}%`}
                      </span>
                    </div>
                    <Slider
                      value={[rules.minBatteryPct]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={(v) => { void patchRules({ minBatteryPct: v[0] ?? 0 }, { silent: true }); }}
                      onValueCommit={(v) => onMinBatteryCommit(v[0] ?? 0)}
                    />
                  </div>

                  <Row>
                    <div className="flex items-center gap-2">
                      <Zap className="size-4 text-[var(--roava-text-muted)]" />
                      <span className="text-sm font-medium text-[var(--roava-text)]">Allow when charging</span>
                    </div>
                    <Switch
                      checked={rules.requireChargingUnderMin}
                      onCheckedChange={(v) => patchRules({ requireChargingUnderMin: v })}
                    />
                  </Row>

                  {rules.dirty && (
                    <p className="text-[11px] text-[var(--roava-text-muted)] mt-2">
                      Saved on this device. Will sync when you're back online.
                    </p>
                  )}
                </div>
              )}
            </RoavaCard>


            <RoavaCard>
              <div className="flex items-center gap-2 mb-3">
                <CloudUpload className="size-4 text-[var(--roava-text-muted)]" />
                <h3 className="text-sm font-semibold text-[var(--roava-text)]">Queue status</h3>
              </div>
              <div className="space-y-1.5 text-xs">
                <KV
                  label="Queued route points (incl. retries)"
                  value={`${outbox.queued.toLocaleString()}${outbox.needsLogin ? " (safe — not lost)" : ""}`}
                />
                <KV label="Last saved to outbox" value={fmtAgoIso(outbox.lastQueuedAt, now)} />
                {nativeQueueStats != null && (
                  <KV
                    label="Native buffer"
                    value={`${nativeQueueStats.native_queue_count} point${nativeQueueStats.native_queue_count === 1 ? "" : "s"}`}
                  />
                )}
                <KV
                  label="Last successful sync"
                  value={`${fmtAgoIso(outbox.lastSyncAt, now)}${!online ? " (offline)" : ""}`}
                />
                {lastSyncAttempt && (!outbox.lastSyncAt || new Date(lastSyncAttempt.at).getTime() > new Date(outbox.lastSyncAt).getTime()) && (
                  <KV
                    label="Last sync attempt"
                    value={`${fmtAgoIso(lastSyncAttempt.at, now)}${lastSyncAttempt.ok ? "" : " · failed"}`}
                  />
                )}
                {cloudPings != null && (
                  <KV
                    label="In cloud for this trip"
                    value={`${cloudPings.count.toLocaleString()} ping${cloudPings.count === 1 ? "" : "s"}${cloudPings.latestAt ? ` · last ${fmtAgoIso(cloudPings.latestAt, now)}` : ""}`}
                  />
                )}
                <p className="text-[10px] text-[var(--roava-text-muted)] leading-relaxed pt-1">
                  Queue counts include automatic retries. The server keeps one row per unique GPS sample (dedup key), so the cloud total is normally lower than what was queued — that's by design, not lost data.
                </p>
              </div>
              {outbox.needsLogin ? (
                <>
                  <p className="mt-3 text-[11px] text-[var(--roava-text-muted)]">
                    Sync paused — sign in to resume.
                  </p>
                  <PrimaryButton
                    size="sm"
                    onClick={handleResumeSync}
                    disabled={resuming}
                    className="mt-2 w-full h-10 rounded-xl"
                  >
                    {resuming ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
                    {resuming ? "Reconnecting…" : "Sign in to resume sync"}
                  </PrimaryButton>
                </>
              ) : online ? (
                <SecondaryButton
                  size="sm"
                  onClick={syncNow}
                  disabled={syncing}
                  className="mt-3 w-full h-10 rounded-xl"
                >
                  {syncing ? <Loader2 className="size-4 mr-2 animate-spin" /> : <CloudUpload className="size-4 mr-2" />}
                  {syncing ? "Syncing…" : "Try syncing now"}
                </SecondaryButton>
              ) : (
                <p className="mt-3 text-[11px] text-[var(--roava-text-muted)]">
                  Will sync when cell or Wi-Fi returns.
                </p>
              )}
            </RoavaCard>
          </section>



          {/* 6. Battery */}
          <RoavaCard>
            <div className="flex items-center gap-2 mb-3">
              {lowBattery ? (
                <BatteryLow className="size-4 text-[var(--roava-orange)]" />
              ) : battery.isCharging === true ? (
                <BatteryCharging className="size-4 text-[var(--roava-green)]" />
              ) : (
                <Battery className="size-4 text-[var(--roava-text-muted)]" />
              )}
              <h3 className="text-sm font-semibold text-[var(--roava-text)]">Battery</h3>
            </div>

            <div className="space-y-1.5 text-xs mb-2">
              <KV label="Level" value={hasBattery ? `${batteryPct}%` : "—"} />
              <KV
                label="Status"
                value={
                  hasBattery
                    ? battery.isCharging === true ? "Charging" : "On battery"
                    : "Unavailable on this device"
                }
              />
            </div>

            {!hasBattery && (
              <p className="text-xs text-[var(--roava-text-muted)] leading-relaxed mb-2">
                Battery info isn't available here. Tracking and uploads still work; the battery rules apply when iOS reports a level.
              </p>
            )}

            {lowBattery ? (
              <p className="text-xs text-[var(--roava-orange)] leading-relaxed">
                Low battery — consider Expedition mode.
              </p>
            ) : (
              <p className="text-xs text-[var(--roava-text-muted)] leading-relaxed">
                Battery saver or Expedition mode is recommended for multi-day hiking. High accuracy drains the battery faster.
              </p>
            )}
          </RoavaCard>

          {/* 7. Diagnostics — only place shell mode appears now. */}
          <RoavaCard>
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="size-4 text-[var(--roava-text-muted)]" />
              <h3 className="text-sm font-semibold text-[var(--roava-text)]">Diagnostics</h3>
            </div>
            <div className="space-y-1.5 text-xs">
              <KV label="Trip" value={trip?.name ?? "Cached trip unavailable"} />
              <KV
                label="Tracking started"
                value={startedAt ? new Date(startedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—"}
              />
              <KV label="Last GPS fix" value={lastPingText} />
              <KV label="Location permission" value={perm} />
              <KV label="Queued route points (outbox, incl. retries)" value={String(outbox.queued)} />
              {nativeQueueStats != null && (
                <>
                  <KV
                    label="Native buffer"
                    value={`${nativeQueueStats.native_queue_count} point${nativeQueueStats.native_queue_count === 1 ? "" : "s"}`}
                  />
                  {nativeQueueStats.oldest_queued_point_at && (
                    <KV
                      label="Oldest in native buffer"
                      value={fmtAgoIso(nativeQueueStats.oldest_queued_point_at, now)}
                    />
                  )}
                  {nativeQueueStats.last_drain_success_at && (
                    <KV
                      label="Last native→outbox drain"
                      value={fmtAgoIso(nativeQueueStats.last_drain_success_at, now)}
                    />
                  )}
                </>
              )}
              <KV label="Shell mode" value={mode} />
              {shellError ? (() => {
                const human = humanizeShellError(shellError);
                return (
                  <>
                    <KV
                      label="Last connection issue"
                      value={`${human.label} · ${fmtAgoIso(shellError.occurredAt, now)}`}
                    />
                    {human.hint && (
                      <p className="text-[11px] text-[var(--roava-text-muted)] -mt-0.5 pl-0.5">
                        {human.hint}
                      </p>
                    )}
                  </>
                );
              })() : null}
            </div>
            <SnapMediaButton tripId={trip?.id ?? null} />
          </RoavaCard>

          {/* 8. Bottom actions */}
          <div className="grid gap-2 pt-2">
            <SecondaryButton
              onClick={tryReconnect}
              disabled={retrying}
              className="h-12 rounded-2xl"
            >
              <RefreshCw className={cn("size-4 mr-2", retrying && "animate-spin")} />
              {retrying ? "Reconnecting…" : "Try reconnecting"}
            </SecondaryButton>
            <Link
              to="/trip"
              className="text-center text-xs text-[var(--roava-text-muted)] py-2 underline-offset-2 hover:underline"
            >
              Back to main app
            </Link>
          </div>
        </div>
      </RoavaPage>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-t border-[var(--roava-border)] first:border-t-0">
      {children}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--roava-text-muted)]">{label}</span>
      <span className="font-medium text-[var(--roava-text)] text-right">{value}</span>
    </div>
  );
}

function GuardStateNote({
  tracking,
  batteryPct,
  charging,
}: {
  tracking: boolean;
  batteryPct: number | null;
  charging: boolean;
}) {
  const mode = getBatteryGuardMode();
  const threshold = getBatteryGuardThresholdPct();
  const allowCharging = getTrackingAllowWhenCharging();
  if (mode === "off") {
    return <p className="mt-2 text-[11px] text-[var(--roava-text-muted)]">Guard is off — tracking will not pause for low battery.</p>;
  }
  if (batteryPct == null) {
    return <p className="mt-2 text-[11px] text-[var(--roava-text-muted)]">Armed at {threshold}% — waiting for battery reading.</p>;
  }
  if (batteryPct > threshold) {
    return (
      <p className="mt-2 text-[11px] text-[var(--roava-text-muted)]">
        Armed — battery {batteryPct}% is above the {threshold}% threshold. {tracking ? "Tracking continues." : "Will apply once tracking starts."}
      </p>
    );
  }
  if (charging && allowCharging) {
    return <p className="mt-2 text-[11px] text-[var(--roava-text-muted)]">Below threshold but charging — guard bypassed.</p>;
  }
  return (
    <p className="mt-2 text-[11px] text-[var(--roava-orange)]">
      Battery {batteryPct}% is at/below {threshold}% — {mode === "pause" ? "tracking paused by guard." : "warning only."}
    </p>
  );
}


function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-9 px-3 rounded-lg text-xs font-medium transition",
        active
          ? "bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)]"
          : "bg-[var(--roava-gray)] text-[var(--roava-text-muted)]",
      )}
    >
      {children}
    </button>
  );
}

// ManualGpsButton was extracted to `@/components/ManualGpsButton` so the Trip
// home tab can reuse the exact same one-shot capture button.


function SnapMediaButton({ tripId }: { tripId: string | null }) {
  const [busy, setBusy] = useState(false);
  async function onTap() {
    if (!tripId || busy) return;
    setBusy(true);
    try {
      const { snapTripNow } = await import("@/lib/media-snap");
      const r = await snapTripNow(tripId);
      if (r.snapped > 0) {
        toast.success(
          `Snapped ${r.snapped} photo${r.snapped === 1 ? "" : "s"} to the GPS route.`,
        );
      } else if (r.scanned === 0) {
        toast("No un-located photos to snap.");
      } else {
        toast(
          `Scanned ${r.scanned} — none had a GPS ping within ${Math.round(r.windowSeconds / 60)} min.`,
        );
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't snap photos.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <SecondaryButton
      onClick={onTap}
      disabled={!tripId || busy}
      className="mt-3 h-10 w-full rounded-xl text-xs font-medium"
    >
      {busy
        ? <><Loader2 className="size-4 mr-2 animate-spin" />Snapping…</>
        : <><MapPin className="size-4 mr-2" />Snap un-located photos to GPS route</>}
    </SecondaryButton>
  );
}

